'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Price from '../price/Price';
import Image from 'next/image';
import { useCampaign } from '@/app/CampaignContext';
import { MODAL_TYPES, useModal } from './ModalProvider';

export default function CartCrossSellModalWrapper({
  isOpen,
  onClose,
  item,
  modalProduct,
  cartPage,
  onAddProduct
}) {
  const modalRef = React.useRef(null);
  const campaign = useCampaign();
  const pricingDetailsData = campaign.pricing_details;
  const { modalUtils, getCloseButton } = useModal();

  React.useEffect(() => {
    if (!isOpen) return;
    const ref = modalRef.current;

    if (ref) {
      ref.focus();
      // Ensure modal content starts at the top
      ref.scrollTop = 0;
    }
  }, [isOpen, onClose]);

  // Ensure modal scrolls to top when opened
  React.useEffect(() => {
    if (isOpen && modalRef.current) {
      // Find the modal container with overflow-auto
      const modalContainer = modalRef.current.closest('.overflow-auto');
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      // Also ensure the modal body itself is at top
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  function handleAddProduct(e) {
    e.preventDefault();
    onClose();
    if (onAddProduct) {
      onAddProduct();
    }

    if (cartPage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  if (!isOpen) return null;
  return modalUtils.createModalWrapper({
    modalType: MODAL_TYPES.cartCrossSell,
    isOpen,
    onClose,
    containerClassName: modalUtils.containerPresets.crossSell.green,
    children: (
      <div ref={modalRef} tabIndex={-1}>
        {getCloseButton(MODAL_TYPES.cartCrossSell, onClose)}
        <div className="sm:flex sm:gap-6 lg:gap-8">
          <div className="relative mx-auto h-[180px] w-[180px] sm:mx-0 lg:h-[220px] lg:w-[220px]">
            <Image
              src={item?.featuredImage.url}
              alt={item?.title.replace(/\.[^/.]+$/, '')}
              fill
              sizes="(min-width: 1024px) 220px, 180px"
              className="object-cover"
            />
          </div>
          <div className="pt-5 sm:ml-0 sm:flex-1 sm:pt-0">
            <div className="h5 sm:h4 text-white">{item?.title}</div>
            <div className="body-s text-dawn-grey pb-2">
              {item.variants[0].title}
            </div>
            <Price
              price={item.variants[0].price.amount}
              compareAtPrice={item.variants[0].compareAtPrice.amount}
              priceClassOverride="price-r text-orange!"
              crossedPriceClassOverride="price-r-crossed text-dawn-grey!"
              variant={`cart-cross-sell-modal-${item.handle}`}
            />
            {pricingDetailsData?.price_details_link && (
              <Link
                href={pricingDetailsData?.price_details_link.href}
                className="body-s text-dawn-grey mt-1 flex w-fit grow-0 cursor-pointer items-center underline"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${pricingDetailsData?.price_details_link.title} - Link opens in a new tab`}
              >
                <span className="mr-1">
                  {pricingDetailsData?.price_details_link.title}
                </span>
                <span className="sr-only">Link opens in a new tab</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  data-name="Layer 2"
                  viewBox="0 0 12 12"
                  width="12"
                  height="12"
                  aria-hidden="true"
                >
                  <g data-name="Layer 1">
                    <path
                      d="M4.152 7.005 10.685.5M7.056.5h3.63v3.614"
                      style={{
                        fill: 'none',
                        stroke: '#a19f9b',
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round'
                      }}
                    />
                    <path
                      d="M4.674 2.345H1.5a1 1 0 0 0-1 1v6.348a1 1 0 0 0 1 1h6.348a1 1 0 0 0 1-1V6.519"
                      style={{
                        fill: 'none',
                        stroke: '#a19f9b',
                        strokeLinecap: 'round'
                      }}
                    />
                  </g>
                </svg>
              </Link>
            )}
            {(() => {
              const productDescriptionBlock =
                modalProduct?.[0]?.product_detail_blocks?.find(
                  (block) => !!block?.product_description
                )?.product_description;
              const tabs = productDescriptionBlock?.tab_settings;
              return Array.isArray(tabs) ? (
                <CartProductTabs data={tabs} selectedPayload={item} />
              ) : null;
            })()}
            {/* Desktop button */}
            <button
              onClick={handleAddProduct}
              className="btn bg-burnt-cognac hover:border-fiery-cognac-120 hover:bg-fiery-cognac-120 hidden h-[48px] w-full text-white transition-colors sm:block"
            >
              Add to order
            </button>
          </div>
        </div>
        {/* Mobile button */}
        <div className="bg-parlor-green-95 sticky bottom-0 z-10 -mr-5 -ml-5 w-[calc(100%+2.5rem)] px-5 py-5 shadow-lg sm:hidden">
          <button
            onClick={handleAddProduct}
            className="btn bg-burnt-cognac hover:border-fiery-cognac-120 hover:bg-fiery-cognac-120 h-[48px] w-full text-white transition-colors"
          >
            Add to order
          </button>
        </div>
      </div>
    )
  });
}

function CartProductTabs({ data, selectedPayload }) {
  const [activeTab, setActiveTab] = useState(0);

  if (!data) {
    console.error(
      'No data provided for cart cross sell tabs:',
      selectedPayload?.title
    );
    return null;
  }

  function toggleTab(e, index) {
    e.preventDefault();
    if (index !== activeTab) {
      setActiveTab(index);
    }
  }

  return (
    <div className="text-default mx-0 mt-2 mb-0 w-full pb-5">
      <div className="flex-end mb-6 flex flex-nowrap text-xl">
        {data.map((tab, index) => (
          <button
            key={index}
            className={`sh12 product-tabs-choice ${
              activeTab === index ? 'active-color active cursor-default' : ''
            }`}
            onClick={(e) => toggleTab(e, index)}
            tabIndex="0"
          >
            {tab.tab_title}
          </button>
        ))}
      </div>

      <div className="border-position border-crisp-white border-b"></div>

      <div>
        {data.map((tab, index) => (
          <div
            key={index}
            className={`body-s product-description-tab text-white ${
              activeTab === index ? 'active cursor-default' : ''
            }`}
          >
            <div dangerouslySetInnerHTML={{ __html: tab.copy }} />
          </div>
        ))}
      </div>
    </div>
  );
}
